import type { ReactNode } from 'react';
import { useState } from 'react';
import { Platform, useWindowDimensions, type ViewStyle } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Card } from '../../../components/primitives/Card';
import { type AccentColor, accentColors, accentGlows } from '../../../theme/accents';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  delay?: number;
  renderActions?: () => ReactNode;
  accentColor?: AccentColor;
  enableHover?: boolean;
}

export function ChartCard({
  title,
  subtitle,
  children,
  delay = 0,
  renderActions,
  accentColor,
  enableHover = true,
}: Readonly<ChartCardProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const borderColor = accentColor ? accentColors[accentColor] : undefined;
  const glowColor = accentColor ? accentGlows[accentColor] : colors.glow.cyan;

  const webHoverStyle = Platform.select({
    web: enableHover
      ? ({
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(isHovered && {
            boxShadow: `0 8px 24px ${glowColor}`,
          }),
        } as ViewStyle)
      : {},
    default: {},
  });

  const hoverProps = Platform.select({
    web: enableHover
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {},
    default: {},
  });

  const cardStyle: ViewStyle = {
    width: '100%',
    alignSelf: 'stretch',
    ...(isDesktop ? { height: '100%' } : null),
    ...(isHovered && borderColor ? { borderColor } : null),
  };

  const animatedCardStyle: ViewStyle = {
    alignSelf: 'stretch',
    width: '100%',
    ...(isDesktop ? { flexGrow: 1 } : null),
  };

  const vstackStyle: ViewStyle | undefined = isDesktop ? { flex: 1 } : undefined;
  const contentBoxStyle: ViewStyle | undefined = isDesktop ? { flex: 1 } : undefined;

  return (
    <AnimatedCard delay={delay} style={animatedCardStyle}>
      <Card
        variant="default"
        padding="xl"
        radius="xl"
        style={[cardStyle, webHoverStyle]}
        {...hoverProps}
      >
        <VStack spacing="lg" style={vstackStyle}>
          <HStack justify="between" align="center">
            <VStack spacing="xs">
              <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
                {title}
              </Text>
              {subtitle && (
                <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 12 }}>
                  {subtitle}
                </Text>
              )}
            </VStack>
            {renderActions?.()}
          </HStack>
          <Box style={contentBoxStyle}>{children}</Box>
        </VStack>
      </Card>
    </AnimatedCard>
  );
}
